// Runtime log — streaming Wireshark-style event console for the sim.
//
// The sim writes events here as state transitions occur (cooling
// engaged, freezestat tripped, sensor open-circuit, NC/NO mismatch).
// The runtime log panel reads + renders. Bounded buffer to avoid
// runaway memory on long runs.

export type LogLevel = 'info' | 'warn' | 'error' | 'critical';

export interface LogEntry {
  readonly id: number;
  /** Sim time in seconds (NOT wall clock). */
  readonly simSec: number;
  readonly level: LogLevel;
  /** Source — typically the controller / sensor / safety node label. */
  readonly source: string;
  readonly message: string;
  /** Optional structured detail for filtering (e.g. nodeId). */
  readonly nodeId?: string;
}

const MAX_ENTRIES = 500;
// Bumped the key suffix when we tightened the viewport clamp — anyone who
// had a position saved from before would otherwise stay stranded near the
// top of the viewport. Loading from the OLD key falls through to the
// default (0, 0) = bottom-right corner.
const LS_POSITION = 'bas-sandbox.runtime-log.position.v2';
const LS_OPEN = 'bas-sandbox.runtime-log.open.v1';

interface RuntimeLogStore {
  entries: LogEntry[];
  panelOpen: boolean;
  /** Level filter — show entries at this level or higher severity. */
  filter: LogLevel | 'all';
  paused: boolean;
  /** Offset from the bottom-right of canvas-area (pixels). User-draggable. */
  offsetX: number;
  offsetY: number;
}

function loadStoredPosition(): { x: number; y: number } {
  if (typeof localStorage === 'undefined') return { x: 0, y: 0 };
  try {
    const raw = localStorage.getItem(LS_POSITION);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw) as { x: number; y: number };
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
  } catch {
    // ignore
  }
  return { x: 0, y: 0 };
}

function loadStoredOpen(): boolean {
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(LS_OPEN);
      if (raw !== null) return raw === '1';
    } catch {
      // ignore
    }
  }
  // No saved preference: open on roomy screens, collapsed on short
  // laptop viewports where the panel covers half the canvas.
  return typeof window === 'undefined' || window.innerHeight >= 900;
}

const _initialPos = loadStoredPosition();

export const runtimeLog = $state<RuntimeLogStore>({
  entries: [],
  panelOpen: loadStoredOpen(),
  filter: 'all',
  paused: false,
  offsetX: _initialPos.x,
  offsetY: _initialPos.y,
});

/** Vertical headroom (px) the panel must keep below the viewport top
 *  so the drag handle stays clickable. Accounts for:
 *   - The app's top brand bar (~64px)
 *   - The build-shell internal padding (~16px)
 *   - BuildCanvas's own top toolbar with sim controls, network pill,
 *     and clock (~80-100px depending on wrap)
 *   - Margin so the panel header doesn't slip under any of those
 *  Conservative 360px keeps the panel reliably draggable from any
 *  reasonable viewport size. Bumped from 250 after the user hit the
 *  "I can't grab the runtime log" bug repeatedly with the smaller value. */
const PANEL_MIN_HEADER_VISIBLE = 360;
// When collapsed the panel is just its ~40px header bar, so it can travel
// almost to the top of the canvas without its header slipping off-screen.
// Reserve only enough to keep the header grabbable. (Expanded still uses the
// full 360 above so the body + header stay on-screen.)
const PANEL_MIN_HEADER_VISIBLE_COLLAPSED = 72;

export function setPanelPosition(x: number, y: number): void {
  const { cx, cy } = clampToViewport(x, y);
  runtimeLog.offsetX = cx;
  runtimeLog.offsetY = cy;
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_POSITION, JSON.stringify({ x: cx, y: cy }));
  } catch {
    // ignore
  }
}

/** Snap the panel back to the default bottom-right corner. Wired to a
 *  button in the header so a stuck panel is always recoverable. */
export function resetPanelPosition(): void {
  setPanelPosition(0, 0);
}

/** Clamp x/y so the panel's header stays clickable on the current
 *  viewport. Bottom-right anchored coords: bigger Y = further from
 *  bottom = closer to top. */
function clampToViewport(x: number, y: number): { cx: number; cy: number } {
  if (typeof window === 'undefined') return { cx: Math.max(0, x), cy: Math.max(0, y) };
  // Collapsed panels can tuck much higher since only the header shows.
  const reserveY = runtimeLog.panelOpen
    ? PANEL_MIN_HEADER_VISIBLE
    : PANEL_MIN_HEADER_VISIBLE_COLLAPSED;
  const maxY = Math.max(0, window.innerHeight - reserveY);
  const maxX = Math.max(0, window.innerWidth - PANEL_MIN_HEADER_VISIBLE);
  return {
    cx: Math.min(maxX, Math.max(0, x)),
    cy: Math.min(maxY, Math.max(0, y)),
  };
}

/** Re-clamp the stored position to the current viewport. Call this on
 *  app mount + on window resize so a saved-from-different-screen
 *  position can't strand the panel off-screen. */
export function rehydratePanelPosition(): void {
  const { cx, cy } = clampToViewport(runtimeLog.offsetX, runtimeLog.offsetY);
  if (cx !== runtimeLog.offsetX || cy !== runtimeLog.offsetY) {
    setPanelPosition(cx, cy);
  }
}

let nextId = 1;

export function log(
  simSec: number,
  level: LogLevel,
  source: string,
  message: string,
  nodeId?: string,
): void {
  if (runtimeLog.paused) return;
  runtimeLog.entries.push({
    id: nextId++,
    simSec,
    level,
    source,
    message,
    nodeId,
  });
  // Trim to bound: drop oldest entries above MAX
  if (runtimeLog.entries.length > MAX_ENTRIES) {
    runtimeLog.entries = runtimeLog.entries.slice(-MAX_ENTRIES);
  }
}

export function clearLog(): void {
  runtimeLog.entries = [];
}

export function togglePanel(): void {
  runtimeLog.panelOpen = !runtimeLog.panelOpen;
  // Expanding may need the panel pulled back down so its header + body stay
  // on-screen (the collapsed clamp allows it much higher up).
  rehydratePanelPosition();
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_OPEN, runtimeLog.panelOpen ? '1' : '0');
  } catch {
    // ignore
  }
}

export function togglePaused(): void {
  runtimeLog.paused = !runtimeLog.paused;
}

export function setFilter(f: LogLevel | 'all'): void {
  runtimeLog.filter = f;
}

const LEVEL_RANK: Record<LogLevel, number> = {
  info: 0,
  warn: 1,
  error: 2,
  critical: 3,
};

/** Entries that should render after applying the active filter. */
export function visibleEntries(): LogEntry[] {
  if (runtimeLog.filter === 'all') return runtimeLog.entries;
  const min = LEVEL_RANK[runtimeLog.filter];
  return runtimeLog.entries.filter((e) => LEVEL_RANK[e.level] >= min);
}
